import { Catalog } from '@albelli/ecom-promotions-editor';
import { validate as validateDiscount } from '@albelli/ecom-promotions-editor/providers';
import { Box, FormControl, Grid, InputAdornment, Link, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { AyceOffer, VendorCampaignStatus } from 'apis/ayceCampaign';
import { getDiscount } from 'apis/promotionsApi';
import BorderedBox from 'components/common/BorderedBox/BorderedBox';
import { CustomColors } from 'components/common/CustomTheme/CustomTheme';
import { FormikDateTimePicker } from 'components/common/Form/FormikDateTimePicker';
import { FormikDiscountEditor } from 'components/common/Form/FormikDiscountEditor';
import { FormikTextField } from 'components/common/Form/FormikTextField';
import ImageUploadInput from 'components/common/Form/ImageUploadInput';
import { isPast } from 'date-fns';
import { FormikErrors, useFormik } from 'formik';
import { isValidDate, parseAtomToDate } from 'helpers/dateHelper';
import { getStorefrontUrl } from 'helpers/urlHelper';
import { DiscountDefinition } from 'models/discounts/discount';
import { Vendor } from 'models/vendor';
import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'store';

type AyceOfferFormProps = {
    offer: AyceOffer | null;
    vendor: Vendor;
    campaignStatus: VendorCampaignStatus;
};

type FormType = {
    discountCode: string;
    imagePath: string;
    imageFile: File | null;
    productBlockTitle: string;
    checkoutLabel: string;
    voucherPrice: number;
    productPrice: number;
    expirationDate: Date | null;
};

export type AyceOfferFormResult = {
    discount: {
        initial: DiscountDefinition | null;
        action: 'create' | 'update' | null;
        code: string;
    };
    image: { file: File } | { path: string };
    productBlockTitle: string;
    checkoutLabel: string;
    voucherPrice: number;
    productPrice: number;
    expirationDate: Date;
};

export interface AyceOfferHandlers {
    /**
     * returns void if invalid
     */
    submit: () => Promise<AyceOfferFormResult | void>;
    // isModified: () => boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        wrapper: {
            padding: theme.spacing(1),
            border: `0.5px solid ${theme.palette.grey[400]}`,
            width: '100%',
        },
        column: {
            height: '100%',
            padding: theme.spacing(1.5),
        },
        title: {
            fontSize: theme.typography.pxToRem(15),
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            marginBottom: theme.spacing(1),
        },
        comment: {
            fontSize: theme.typography.pxToRem(13),
            marginBottom: theme.spacing(1),
            color: '#676767',
            lineHeight: 1.2,
        },
        discountEditorContainer: {
            border: `1px solid ${CustomColors.border}`,
            borderRadius: '3px',
        },
        discountError: {
            borderColor: theme.palette.error.main,
        },
    }),
);

const validate = (values: FormType, catalog: Catalog) => {
    const errors: FormikErrors<FormType> = {};

    if (!values.discountCode.trim()) {
        errors.discountCode = 'Discount code is required';
    } else {
        const codeErrors = validateDiscount(values.discountCode, catalog);
        if (codeErrors.length) {
            errors.discountCode = codeErrors.map(e => e.message).join('\n');
        }
    }

    if (!values.imagePath && !values.imageFile) {
        errors.imageFile = 'Image is required';
    }
    if (!values.productBlockTitle) {
        errors.productBlockTitle = 'Product block title is required';
    }
    if (!values.checkoutLabel) {
        errors.checkoutLabel = 'Checkout label is required';
    }
    if (!values.voucherPrice) {
        errors.voucherPrice = 'Voucher price is required';
    } else if (values.voucherPrice <= 0) {
        errors.voucherPrice = 'Voucher price must be greater than 0';
    } else if (values.productPrice && values.voucherPrice > values.productPrice) {
        errors.voucherPrice = 'Voucher price must be less than product price';
    }
    if (!values.productPrice) {
        errors.productPrice = 'Product price is required';
    } else if (values.productPrice <= 0) {
        errors.productPrice = 'Product price must be greater than 0';
    }
    if (!values.expirationDate) {
        errors.expirationDate = 'Expiration date is required';
    } else if (!isValidDate(values.expirationDate)) {
        errors.expirationDate = 'Expiration date should be a valid date';
    } else if (isPast(values.expirationDate)) {
        errors.expirationDate = 'Expiration date should not be in the past';
    }

    return errors;
};

const offerToForm = (data: AyceOffer, code = ''): FormType => ({
    discountCode: code,
    imagePath: data.imagePath,
    imageFile: null,
    productBlockTitle: data.productBlockTitle,
    checkoutLabel: data.checkoutLabel,
    voucherPrice: data.voucherPrice,
    productPrice: data.productPrice,
    expirationDate: parseAtomToDate(data.expirationDate),
});

function AyceOfferForm(
    { offer, vendor, campaignStatus = VendorCampaignStatus.DRAFT }: AyceOfferFormProps,
    ref,
): React.ReactElement {
    const classes = useStyles();
    const catalog = useSelector(state => state.promotionCatalog);
    const [discountDefinition, setDiscountDefinition] = useState<DiscountDefinition | null>(null);

    const formik = useFormik<FormType>({
        initialValues: {
            discountCode: '',
            imagePath: '',
            imageFile: null,
            productBlockTitle: '',
            checkoutLabel: '',
            voucherPrice: 0,
            productPrice: 0,
            expirationDate: null,
        },
        onSubmit: async _ => _,
        validate: (values: FormType) => validate(values, catalog.payload),
    });

    useEffect(() => {
        if (!offer) return;
        formik.setValues(offerToForm(offer));

        let isMounted = true;

        (async (id: string) => {
            try {
                const discount = await getDiscount(id);
                if (!isMounted) return;
                if (discount) {
                    setDiscountDefinition(discount);
                    formik.setFieldValue('discountCode', discount.definition);
                } else {
                    formik.setFieldValue('discountCode', 'Does not exist anymore');
                }
            } catch (error) {
                isMounted && formik.setFieldValue('discountCode', 'Failed to load');
            }
        })(offer.discountId);

        return () => {
            isMounted = false;
        };
    }, [offer]);

    const isDisabled = campaignStatus !== VendorCampaignStatus.DRAFT && !!offer;

    useImperativeHandle(
        ref,
        (): AyceOfferHandlers => ({
            submit: async () => {
                if (isDisabled && offer) {
                    // in case if expirationDate in the past now
                    return {
                        discount: {
                            initial: discountDefinition,
                            action: null,
                            code: '',
                        },
                        image: {
                            path: offer.imagePath,
                        },
                        productBlockTitle: offer.productBlockTitle,
                        checkoutLabel: offer.checkoutLabel,
                        voucherPrice: offer.voucherPrice,
                        productPrice: offer.productPrice,
                        expirationDate: parseAtomToDate(offer.expirationDate),
                    };
                }
                const vals = (await formik.submitForm()) as FormType | null;
                if (!vals) return;
                const formResult: AyceOfferFormResult = {
                    discount: {
                        initial: discountDefinition,
                        action: discountDefinition
                            ? discountDefinition.definition === vals.discountCode
                                ? null
                                : 'update'
                            : 'create',
                        code: vals.discountCode,
                    },
                    image: vals.imageFile ? { file: vals.imageFile } : { path: vals.imagePath },
                    productBlockTitle: vals.productBlockTitle,
                    checkoutLabel: vals.checkoutLabel,
                    voucherPrice: vals.voucherPrice,
                    productPrice: vals.productPrice,
                    expirationDate: vals.expirationDate as Date,
                };
                return formResult;
            },
            // isModified: () => {
            //     const initialValues = offer ? offerToForm(offer, discountDefinition?.definition) : formik.initialValues;
            //     return !_.isEqual(initialValues, formik.values);
            // },
        }),
    );

    return (
        <Box className={classes.wrapper} data-cy="Ayce-OfferForm">
            <Grid container spacing={2}>
                <Grid item sm={6}>
                    <BorderedBox className={classes.column}>
                        <Box component="h3" className={classes.title}>
                            Create discount definition
                        </Box>
                        <Typography className={classes.comment} component="p">
                            See the{' '}
                            <Link
                                href="https://wiki.albelli.net/wiki/Discount_definition_FAQ"
                                target="_blank"
                                rel="noreferrer"
                                underline="always"
                            >
                                FAQ
                            </Link>{' '}
                            for more information on discounts, only 1 PAP allowed for AYCE
                        </Typography>
                        <FormikDiscountEditor
                            formik={formik}
                            name="discountCode"
                            disabled={isDisabled}
                            height="110px"
                            cy="Ayce-OfferForm-DiscountEditor"
                        />
                        <Box component="h3" className={classes.title}>
                            Offer image*
                        </Box>
                        <FormControl error={!!formik.errors.imageFile}>
                            <ImageUploadInput
                                name="offerImg"
                                buttonText="select image"
                                previewWidth={137}
                                fileValue={null}
                                filePath={getStorefrontUrl(vendor.name, offer?.imagePath)}
                                error={!!formik.errors.imageFile}
                                helperText={formik.touched.imageFile ? formik.errors.imageFile : ''}
                                handleChange={file => {
                                    formik.setFieldValue('imageFile', file);
                                }}
                                disabled={isDisabled}
                                allowDeletion={false}
                            />
                        </FormControl>
                    </BorderedBox>
                </Grid>
                <Grid item sm={6}>
                    <BorderedBox className={classes.column}>
                        <Box component="h3" className={classes.title}>
                            Offer details
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item sm={12}>
                                <FormikTextField
                                    label="Product block title"
                                    name="productBlockTitle"
                                    fullWidth
                                    required
                                    formik={formik}
                                    disabled={isDisabled}
                                    data-cy="Ayce-OfferForm-ProductBlockTitleInput"
                                />
                            </Grid>

                            <Grid item sm={12}>
                                <FormikTextField
                                    label="Checkout label (shown in the Basket page)"
                                    name="checkoutLabel"
                                    fullWidth
                                    required
                                    formik={formik}
                                    disabled={isDisabled}
                                    data-cy="Ayce-OfferForm-CheckoutLabelInput"
                                />
                            </Grid>

                            <Grid item sm={6} md={3}>
                                <FormikTextField
                                    label="Voucher price"
                                    name="voucherPrice"
                                    type="number"
                                    fullWidth
                                    required
                                    formik={formik}
                                    disabled={isDisabled}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">{vendor.currency.symbol}</InputAdornment>
                                        ),
                                    }}
                                    data-cy="Ayce-OfferForm-VoucherPriceInput"
                                />
                            </Grid>
                            <Grid item sm={6} md={3}>
                                <FormikTextField
                                    label="Product price"
                                    name="productPrice"
                                    type="number"
                                    fullWidth
                                    required
                                    formik={formik}
                                    disabled={isDisabled}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">{vendor.currency.symbol}</InputAdornment>
                                        ),
                                    }}
                                    data-cy="Ayce-OfferForm-ProductPriceInput"
                                />
                            </Grid>
                            <Grid item sm={6} md={6}>
                                <FormikDateTimePicker
                                    label="Discount expiry date"
                                    name="expirationDate"
                                    fullWidth
                                    required
                                    formik={formik}
                                    disabled={isDisabled}
                                    data-cy="Ayce-OfferForm-ExpiryDate"
                                />
                            </Grid>
                        </Grid>
                    </BorderedBox>
                </Grid>
            </Grid>
        </Box>
    );
}

export default forwardRef(AyceOfferForm);
